// Aplica el "color principal" que cada negocio elige en Configuración a toda
// la interfaz. Antes este color solo se guardaba en la base de datos pero
// nunca se usaba para nada: los botones y acentos (`bg-brand-500`, etc.)
// salían de una paleta fija definida en tailwind.config.js, compilada en
// tiempo de build — no había forma de que un color elegido en tiempo real
// cambiara esas clases. Ahora tailwind.config.js define esos mismos tonos
// como variables CSS con un valor de respaldo (el teal original), y esta
// función calcula el resto de la escala (claros/oscuros) a partir del color
// que elige el negocio y los aplica como variables en <html>.

function hexToHsl(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const num = parseInt(m[1], 16);
  const r = ((num >> 16) & 255) / 255;
  const g = ((num >> 8) & 255) / 255;
  const b = (num & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / d) % 6;
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// Tonos de luminosidad (0-100) para cada escala, tomando como base la
// luminosidad real del color elegido en el 500 (el que más se usa en
// botones y acentos) y aclarando/oscureciendo alrededor de ese valor.
function escalaDesdeBase(l500: number) {
  return {
    50: 95,
    100: 90,
    200: 80,
    300: 70,
    400: 60,
    500: Math.min(Math.max(l500, 20), 65),
    600: Math.max(l500 - 12, 8),
    700: Math.max(l500 - 22, 6),
    800: Math.max(l500 - 30, 4),
    900: Math.max(l500 - 36, 3),
  };
}

const DEFECTO = "#0EA5A4";

export function aplicarColorMarca(colorHex?: string | null) {
  const hsl = hexToHsl(colorHex || DEFECTO) ?? hexToHsl(DEFECTO)!;
  const [h, s, l] = hsl;
  const escala = escalaDesdeBase(l);
  const root = document.documentElement;
  for (const [tono, luminosidad] of Object.entries(escala)) {
    root.style.setProperty(`--brand-${tono}`, hslToHex(h, s, luminosidad));
  }
}
